import React, { FC, useCallback } from 'react';
import {
  BasicContainer,
  BodyText,
  BodyTextLarge,
  ButtonProps,
  FlexCol,
  FlexRow,
  GridDynamic,
  TextDynamic,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { appConfig as globalAppConfig } from '@minecraft.app-config';
import { Trans, TranslateFn, useTranslation } from '@blocs.i18n';
import Modal from '@minecraft.modal';
import { PaywallLocation, useBillingPopup } from '@blocs.paywall';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { SocialMediaPlatform } from '@minecraft.graphql-operations';
import { useDeviceAndBrowserInfo } from '@minecraft.utils';
import { useProfileViewContext } from '../ProfileViewContext';

const PrivacyPolicyLink: FC = ({ children }) => (
  <a href="https://www.castingnetworks.com/privacy" target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

type FooterItem = {
  icon: string;
  title: string;
  description: React.ReactNode;
};

const FOOTER_ITEMS = (t: TranslateFn): FooterItem[] => [
  {
    icon: 'switch-accounts',
    title: t('talent:profileEdit.social.consentModal.footer.itemHeader1'),
    description: t('talent:profileEdit.social.consentModal.footer.itemDescription1'),
  },
  {
    icon: 'lock',
    title: t('talent:profileEdit.social.consentModal.footer.itemHeader2'),
    description: (
      <Trans
        i18nKey="talent:profileEdit.social.consentModal.footer.itemDescription2"
        components={{
          a: <PrivacyPolicyLink />,
        }}
      />
    ),
  },
];

const FooterItemTemplate = ({ item }: { item: FooterItem }) => {
  return (
    <FlexCol w="full" data-testid="footer-item">
      <FlexRow w="full" gap="2" alignItems="baseline">
        <Icon name={item.icon} size="sm" />
        <BodyTextLarge fontWeight="semibold">{item.title}</BodyTextLarge>
      </FlexRow>
      <BodyText>{item.description}</BodyText>
    </FlexCol>
  );
};

const FEATURE_LIST_ITEMS = (t: TranslateFn): { key: string; component: React.ReactNode }[] => [
  {
    key: t('talent:profileEdit.social.consentModal.feature1'),
    component: <Trans i18nKey="talent:profileEdit.social.consentModal.feature1" />,
  },
  {
    key: t('talent:profileEdit.social.consentModal.feature2'),
    component: <Trans i18nKey="talent:profileEdit.social.consentModal.feature2" />,
  },
  {
    key: t('talent:profileEdit.social.consentModal.feature3'),
    component: <Trans i18nKey="talent:profileEdit.social.consentModal.feature3" />,
  },
];

const FeatureListItemTemplate = ({ item }: { item: React.ReactNode }) => (
  <FlexRow wrap="nowrap" role="listitem" gap="4" data-testid="social-feature-item">
    <BasicContainer>
      <Icon name="diamondoutline" themeColor="success" size="md" />
    </BasicContainer>
    <BodyText>{item}</BodyText>
  </FlexRow>
);

type ConsentModalProps = {
  isOpen: boolean;
  close: () => void;
  canAccess: boolean;
};

const ConsentModal: FC<ConsentModalProps> = ({ isOpen, close, canAccess }) => {
  const { t } = useTranslation();
  const billingPopup = useBillingPopup();
  const context = useProfileViewContext();
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();
  const { updater } = context;

  const openBilling = useCallback(() => {
    billingPopup.show({
      location: PaywallLocation.SOCIAL_UPGRADE,
    });
  }, [billingPopup]);

  const modalButtons: ButtonProps[] = [
    {
      label: t('common:button.notNow'),
      onClick: close,
      variant: 'secondary',
    },
    canAccess
      ? {
          label: t('talent:profileEdit.social.consentModal.button.connectAndShare'),
          onClick: async () => {
            captureHeapEvent({
              name: HEAP_EVENT_NAMES.TALENT_SOCIAL_ADD_LINK,
              data: {
                socialPlatform: SocialMediaPlatform.Instagram,
                timestamp: new Date().toISOString(),
                device: deviceInfo,
                browser: browserInfo,
              },
            });
            await updater.handleInstagramAuth();
            close();
          },
          disabled: updater.mutationLoading,
        }
      : {
          onClick: openBilling,
          startIcon: { name: 'rocket', justify: 'end' },
          endIcon: undefined,
          fixedWidth: true,
          label: { name: t('common:membership.upgradeToPremiumShort'), justify: 'start' },
          color: 'hotTrialPaywall',
        },
  ];

  const featureItems = FEATURE_LIST_ITEMS(t);
  const footerItems = FOOTER_ITEMS(t);

  return (
    <Modal
      title=""
      data-testid="instagram-auth-modal"
      isHeaderHidden
      bodyProps={{ className: 'cn_atom_p-0' }}
      isOpen={isOpen}
      modalSize="medium"
      handleToggleClick={close}
      buttons={modalButtons}
    >
      <FlexCol gap="8" className="cn_mx-8 cn_mt-8">
        <FlexRow justifyContent="center">
          <TextDynamic
            fontSize="h1"
            fontWeight="semibold"
            color="default"
            align="left"
            className="cn_w-full"
            mdProps={{ fontSize: 'xl', fontWeight: 'semibold', align: 'center', className: 'cn_w-4/5' }}
          >
            <Icon name="profile-social-instagram" size="xl" className="mr-2" />
            {canAccess
              ? t('talent:profileEdit.social.consentModal.title.premiumUser')
              : t('talent:profileEdit.social.consentModal.title')}
          </TextDynamic>
          <BasicContainer className="cn_mt-4">
            <BodyText>{t('talent:profileEdit.social.consentModal.description')}</BodyText>
          </BasicContainer>
        </FlexRow>
        <GridDynamic
          columns="1fr"
          gap="8"
          mdProps={{ columns: 'repeat(2, 1fr)', className: 'cn_mt-4 cn_mx-8' }}
          alignItems="center"
        >
          <BasicContainer gridRowStart={2} smProps={{ gridRowStart: undefined }}>
            <img
              src={`${globalAppConfig.cdnRoot}/images/static/profile-social-preview.png`}
              srcSet={`${globalAppConfig.cdnRoot}/images/static/profile-social-preview-4x.png`}
              alt={t('talent:profileEdit.social.consentModal.previewImageAlt')}
              className="cn_w-full"
            />
          </BasicContainer>
          <FlexCol
            className="cn_atom_rounded-xl cn_atom_p-4 cn_h-full"
            bgColor="discountOfferBg"
            wrap="nowrap"
            gap="4"
            data-testid="social-features"
            gridRowStart={1}
            smProps={{ gridRowStart: undefined }}
          >
            <BodyText>
              {canAccess
                ? t('talent:profileEdit.social.consentModal.featureHeader.premiumUser')
                : t('talent:profileEdit.social.consentModal.featureHeader')}
            </BodyText>
            {featureItems.map((item) => (
              <FeatureListItemTemplate key={item.key} item={item.component} />
            ))}
          </FlexCol>
        </GridDynamic>
        <FlexRow gap="8" mdProps={{ mx: '8' }} data-testid="social-footer">
          {footerItems.map((item) => (
            <FooterItemTemplate key={item.title} item={item} />
          ))}
        </FlexRow>
      </FlexCol>
    </Modal>
  );
};

export default ConsentModal;
